/**
 * The build console — CodeBuild's output, tailed the same way a run's is.
 *
 * A build in progress streams over SSE; a finished one is read with a single paged
 * fetch. Mirrors `useRunConsole` deliberately: the underlying mechanism (CloudWatch
 * Logs, one group/stream pair) is identical, only the resource is a build instead of a
 * run.
 */

import { useEffect, useState } from "react";

import { endpoints } from "@/api/endpoints";
import type { ConsoleLine } from "@/hooks/useRunConsole";

interface State {
  lines: ConsoleLine[];
  finished: boolean;
  error: string | undefined;
}

const EMPTY: State = { lines: [], finished: false, error: undefined };

export interface BuildConsoleState extends State {
  streaming: boolean;
  complete: boolean;
}

export function useBuildConsole(
  projectSlug: string,
  scriptRef: string,
  buildId: string,
  isLive: boolean,
): BuildConsoleState {
  const [state, setState] = useState<State>(EMPTY);

  useEffect(() => {
    setState(EMPTY);
    let cancelled = false;

    if (!isLive) {
      const load = async () => {
        try {
          let token: string | undefined;
          const collected: ConsoleLine[] = [];
          for (let page = 0; page < 200; page += 1) {
            const result = await endpoints.buildLogs(projectSlug, scriptRef, buildId, token);
            collected.push(...result.lines);
            if (!result.next_token || result.next_token === token || result.lines.length === 0) {
              break;
            }
            token = result.next_token;
          }
          if (!cancelled) {
            setState({ lines: collected, finished: true, error: undefined });
          }
        } catch (caught) {
          if (!cancelled) {
            setState((current) => ({ ...current, finished: true, error: (caught as Error).message }));
          }
        }
      };
      void load();
      return () => {
        cancelled = true;
      };
    }

    const source = new EventSource(
      `/api/projects/${encodeURIComponent(projectSlug)}/scripts/${encodeURIComponent(scriptRef)}/builds/${buildId}/logs/stream`,
      { withCredentials: true },
    );

    source.addEventListener("line", (event) => {
      const line = JSON.parse((event as MessageEvent).data) as ConsoleLine;
      setState((current) => ({ ...current, lines: [...current.lines, line] }));
    });

    source.addEventListener("status", (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as { message?: string };
      if (!payload.message) return;
      setState((current) => ({
        ...current,
        lines: [...current.lines, { ts: Date.now(), message: `— ${payload.message}` }],
      }));
    });

    source.addEventListener("end", () => {
      setState((current) => ({ ...current, finished: true }));
      source.close();
    });

    source.addEventListener("error", (event) => {
      const data = (event as MessageEvent).data;
      if (!data) return;
      let message = "The console stopped unexpectedly.";
      try {
        message = (JSON.parse(data) as { message?: string }).message ?? message;
      } catch {
        // Keep the fallback.
      }
      setState((current) => ({ ...current, error: message, finished: true }));
    });

    return () => {
      cancelled = true;
      source.close();
    };
  }, [projectSlug, scriptRef, buildId, isLive]);

  return {
    ...state,
    streaming: isLive && !state.finished,
    complete: state.finished,
  };
}
